// src/ui/steps/step2-niveau.jsx — Bevestig variant (3.1, alleen festival met
// varianten) en zwaarteniveau (3.2). Voor film/AV en festival-rollen zonder
// varianten valt 3.1 weg en wordt direct de niveau-pagina getoond.
import React from "react";
import { FW, FwButton, FwInlineBanner, StepHeader, AsideCard } from "../primitives";
import { aiHelp } from "../../lib/ai";
import { config } from "../../lib/config";
import { niveauIndicatorenFilmAv, sectieLabels } from "../../lib/niveau-indicatoren";
import { opdrachtKompasFilmAv } from "../../lib/opdracht-kompas";
import { FAMILIE_CRITERIA, bepaalVariant, hasCriteria } from "../../lib/festival-criteria";

// Varianten A t/m E komen uit het Functiehandboek Filmfestivals 2.0.
// A is altijd de laagste schaal, hogere letter = hogere schaal. Het aantal
// varianten verschilt per familie (Medewerker: A/B; Hoofd: A/B/C; Directie:
// A t/m E). De keuze volgt uit de criteria-vragen of een directe variant-knop.
const VARIANT_LETTERS = ["A", "B", "C", "D", "E"];

export const StepNiveau = ({ state, setState, next, back }) => {
  const isFestival = state.type === "festival";
  const tariefrijen = isFestival ? config.tarieven_festival : config.tarieven_film_av;
  const huidigeRol = state.rollen?.[0];
  const heeftVarianten = isFestival && !!huidigeRol?.heeftVarianten;

  // Sub-step state: 3.1 = 'variant' (alleen voor heeftVarianten), 3.2 = 'schaal'.
  // Bij re-entry vanuit step-progress-bar of als gebruiker al een variant heeft
  // gekozen, slaan we 3.1 niet over — gebruiker kan altijd terug om te wijzigen.
  const [subStep, setSubStep] = React.useState(heeftVarianten ? "variant" : "schaal");

  const [niveau, setNiveau] = React.useState(huidigeRol?.niveau);
  const [aiAdvies, setAiAdvies] = React.useState(null);
  const [loadingAi, setLoadingAi] = React.useState(false);
  const [aiError, setAiError] = React.useState(false);

  // Criteria-antwoorden voor de variant-substep. Initieel uit state zodat
  // re-entry (back vanuit 3.2 of via progress-bar) de antwoorden behoudt.
  // Wisselen tussen 3.1 ↔ 3.2 verliest ook geen data; en localStorage in
  // main.jsx persisteert tussen sessies. Wordt gewist bij rol-wisseling
  // (zie step1-rol bevestig-logic).
  const [hulpAntwoorden, setHulpAntwoorden] = React.useState(
    huidigeRol?.criteriaAntwoorden || {}
  );

  // Antwoorden naar parent state pushen.
  React.useEffect(() => {
    setState(s => ({
      ...s,
      rollen: (s.rollen || []).map((r, i) =>
        i === 0 ? { ...r, criteriaAntwoorden: hulpAntwoorden } : r
      ),
    }));
  }, [hulpAntwoorden]);

  // Niveaus die de Gids Rollen aan deze rol toekent.
  const gidsNiveaus = React.useMemo(() => {
    if (!huidigeRol) return [];
    if (Array.isArray(huidigeRol.niveaus) && huidigeRol.niveaus.length) return huidigeRol.niveaus;
    return huidigeRol.niveau ? [huidigeRol.niveau] : [];
  }, [huidigeRol]);

  React.useEffect(() => {
    if (!huidigeRol) { back(); return; }
    if (!(state.omschrijving && state.omschrijving.length > 10 && !aiAdvies)) return;
    const ctrl = new AbortController();
    setLoadingAi(true);
    aiHelp.suggestZwaarte(state.omschrijving, huidigeRol?.rol, isFestival, { signal: ctrl.signal }, gidsNiveaus).then(r => {
      if (ctrl.signal.aborted) return;
      setLoadingAi(false);
      if (r) setAiAdvies(r);
      else setAiError(true);
    });
    return () => ctrl.abort();
  }, []);

  // Sync niveau-keuze direct naar parent state — bewaart selectie bij stap-wisselen
  React.useEffect(() => {
    if (!huidigeRol || niveau === undefined || niveau === huidigeRol.niveau) return;
    setState(s => {
      const r0 = s.rollen?.[0];
      // Detecteer: niveau is via variant-keuze geset (variant-schaal matcht).
      const isVariantKeuze = r0?.festivaltype
        && r0?.variant_schalen?.[r0.festivaltype] === niveau;
      const bron = isVariantKeuze
        ? "variant"
        : (aiAdvies?.niveau === niveau ? "ai-advies" : "handmatig");
      return {
        ...s,
        rollen: (s.rollen || []).map((r, i) =>
          i === 0 ? { ...r, niveau, niveau_bron: bron } : r
        ),
      };
    });
  }, [niveau]);

  if (!huidigeRol) return null;

  const familieCriteria = heeftVarianten && huidigeRol?.familie
    ? FAMILIE_CRITERIA[huidigeRol.familie]
    : null;
  const hulpResultaat = familieCriteria
    ? bepaalVariant(hulpAntwoorden, huidigeRol.familie)
    : null;
  const beantwoordCriterium = (criteriumId, keuze) => {
    setHulpAntwoorden(prev => ({ ...prev, [criteriumId]: keuze }));
  };

  // Variant-keuze (A t/m E) → bepaalt de schaal binnen de gekozen familie.
  // Niet meer aanroepbaar via UI-knop; volgt automatisch uit criteria-scoring.
  const kiesVariant = React.useCallback((letter) => {
    const schaal = huidigeRol?.variant_schalen?.[letter];
    if (!schaal) return;
    setNiveau(schaal);
    setState(s => ({
      ...s,
      rollen: (s.rollen || []).map((r, i) =>
        i === 0 ? { ...r, festivaltype: letter, niveau_bron: "variant" } : r
      ),
    }));
  }, [huidigeRol?.variant_schalen, setState]);

  // Auto-apply: bij elke nieuwe variant-aanbeveling de schaal direct overnemen.
  React.useEffect(() => {
    if (!hulpResultaat?.variant) return;
    if (huidigeRol?.festivaltype === hulpResultaat.variant) return;
    kiesVariant(hulpResultaat.variant);
  }, [hulpResultaat?.variant, huidigeRol?.festivaltype, kiesVariant]);

  // Festival: schalen 1, 2 en 4 hebben geen functies in het Matrix-systeem
  // (Q.S.4 in seed-migratie 00015). Toon ze gedisabled in de niveau-grid.
  const festivalBezetteSchalen = React.useMemo(() => {
    if (!isFestival) return null;
    const s = new Set();
    for (const r of config.rollen_festival || []) {
      for (const n of r.niveaus || []) s.add(n);
    }
    return s;
  }, [isFestival]);

  const huidige = tariefrijen.find(t => t.niveau === niveau);
  const isValide = !!huidige;
  const isNiveauVergrendeld = (n) => {
    if (isFestival) return festivalBezetteSchalen && !festivalBezetteSchalen.has(n);
    return gidsNiveaus.length > 0 && !gidsNiveaus.includes(n);
  };
  const adviesValide = aiAdvies
    && tariefrijen.some(t => t.niveau === aiAdvies.niveau)
    && !isNiveauVergrendeld(aiAdvies.niveau);

  const bevestig = () => {
    setState(s => ({
      ...s,
      rollen: (s.rollen || []).map((r, i) =>
        i === 0 ? { ...r, niveau, niveau_bron: aiAdvies?.niveau === niveau ? "ai-advies" : (huidigeRol?.niveau === niveau ? "rol-lookup" : "handmatig") } : r
      ),
    }));
    next();
  };

  // Routing: 3.1 = variant-sub-pagina, 3.2 = schaal-sub-pagina.
  if (subStep === "variant") {
    return (
      <VariantSubPagina
        huidigeRol={huidigeRol}
        familieCriteria={familieCriteria}
        antwoorden={hulpAntwoorden}
        resultaat={hulpResultaat}
        tariefrijen={tariefrijen}
        onBeantwoord={beantwoordCriterium}
        onTerug={back}
        onVerder={() => setSubStep("schaal")}
      />
    );
  }

  return (
    <SchaalSubPagina
      isFestival={isFestival}
      vorm={state.vorm}
      heeftVarianten={heeftVarianten}
      huidigeRol={huidigeRol}
      tariefrijen={tariefrijen}
      gidsNiveaus={gidsNiveaus}
      niveau={niveau}
      setNiveau={setNiveau}
      isValide={isValide}
      huidige={huidige}
      isNiveauVergrendeld={isNiveauVergrendeld}
      loadingAi={loadingAi}
      aiAdvies={aiAdvies}
      aiError={aiError}
      setAiError={setAiError}
      adviesValide={adviesValide}
      onTerug={() => heeftVarianten ? setSubStep("variant") : back()}
      onBevestig={bevestig}
    />
  );
};

// ============================================================
// 3.1 — Variant-sub-pagina (alleen festival, alleen heeftVarianten)
// ============================================================
const VariantSubPagina = ({
  huidigeRol, familieCriteria, antwoorden, resultaat,
  tariefrijen,
  onBeantwoord, onTerug, onVerder,
}) => {
  const letters = VARIANT_LETTERS.filter(l => huidigeRol.variant_schalen[l] != null);
  const kwalitatief = familieCriteria?.type === "kwalitatief";
  const voorstelVariant = resultaat?.variant || null;
  const voorstelSchaal = voorstelVariant ? huidigeRol.variant_schalen[voorstelVariant] : null;
  const voorstelTarief = voorstelSchaal ? tariefrijen.find(t => t.niveau === voorstelSchaal) : null;

  return (
    <div>
      <StepHeader
        tag="Schaal · Variant"
        title="Welke variant past bij jouw rol?"
        sub={`Het Functiehandboek Filmfestivals onderscheidt voor ${huidigeRol.rol} ${letters.length} varianten (${letters.join(", ")}). Beantwoord de criteria-vragen — je variant volgt automatisch uit je antwoorden.`}
        onBack={onTerug}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 12 }}>
            Je rol: <span style={{ fontWeight: 400 }}>{huidigeRol.rol}</span> · {huidigeRol.dep}
          </div>

          {!familieCriteria && (
            <FwInlineBanner type="warning" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Geen criteria-vragen beschikbaar voor deze rol.</div>
              <div style={{ fontSize: 13 }}>Ga terug en kies een andere rol — of meld dit bij Filmwerkwijs.</div>
            </FwInlineBanner>
          )}

          {familieCriteria && (
            <div style={{ background: FW.white, borderRadius: 20, padding: 22, border: `1.5px solid ${FW.gray100}`, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: FW.navy, marginBottom: 4 }}>
                Criteria-vragen uit het Functiehandboek
              </div>
              <div style={{ fontSize: 13, color: FW.navy2, marginBottom: 14, lineHeight: 1.5 }}>
                Beantwoord per criterium het antwoord dat het best bij jouw rol past.
              </div>
              <div style={{ display: "grid", gap: 14 }}>
                {familieCriteria.criteria.map((c, idx) => (
                  <div key={c.id} style={{ padding: 16, background: FW.cream, borderRadius: 12, border: `1.5px solid ${FW.gray100}` }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 4 }}>
                      {idx + 1}. {c.label}
                    </div>
                    {c.toelichting && (
                      <div style={{ fontSize: 12, color: FW.gray500, marginBottom: 10, lineHeight: 1.4 }}>{c.toelichting}</div>
                    )}
                    <div style={{ display: "grid", gap: 6 }}>
                      {c.keuzes.map((k, ki) => {
                        const isGekozen = kwalitatief
                          ? antwoorden[c.id]?.variant === k.variant
                          : antwoorden[c.id]?.niveau === k.niveau;
                        return (
                          <button key={ki} onClick={() => onBeantwoord(c.id, k)} style={{
                            background: isGekozen ? FW.navy : FW.white,
                            color: isGekozen ? FW.yellow : FW.navy,
                            border: `1.5px solid ${isGekozen ? FW.navy : FW.gray100}`,
                            borderRadius: 10, padding: "10px 14px", cursor: "pointer",
                            fontFamily: "var(--font-sans)", fontSize: 13, lineHeight: 1.4,
                            textAlign: "left",
                          }}>{k.label}</button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {voorstelVariant && (
            <div style={{
              background: FW.taupe, borderRadius: 20, padding: 22,
              border: `1.5px solid ${FW.taupe}`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: FW.navy2, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                Aanbeveling op basis van je antwoorden
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: FW.navy }}>
                Variant {voorstelVariant}
                {voorstelSchaal != null && (
                  <span style={{ fontSize: 14, fontWeight: 500, color: FW.navy2, marginLeft: 8 }}>
                    schaal {voorstelSchaal}{voorstelTarief && ` · € ${voorstelTarief.tarief_min.toFixed(2)}/u`}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: FW.navy2, marginTop: 6 }}>{resultaat.uitleg}</div>
            </div>
          )}

          {!voorstelVariant && familieCriteria && resultaat?.beantwoord === familieCriteria.criteria.length && (
            <div style={{
              background: "#FEF9B3", borderRadius: 20, padding: 22,
              border: `1.5px solid ${FW.yellow}`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: FW.navy2, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <i className="ri-error-warning-line" /> Geen duidelijke variant
              </div>
              <div style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.5 }}>{resultaat.uitleg}</div>
            </div>
          )}
        </div>

        <AsideCard>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i className="ri-stack-line" /> Varianten {letters.join("/")}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: FW.navy2 }}>
            Het Functiehandboek Filmfestivals onderscheidt per functie meerdere varianten — voor <b>{huidigeRol.rol}</b> zijn dat <b>{letters.join(", ")}</b>. <b>{letters[0]}</b> is de laagste schaal; hogere letters horen bij meer verantwoordelijkheid en een hoger starttarief.
          </div>
        </AsideCard>
      </div>

      <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        <FwButton variant="ghost" arrow={false} onClick={onTerug}>← Terug</FwButton>
        <FwButton variant="primary" onClick={onVerder} disabled={!voorstelVariant}>
          Verder naar zwaarteniveau
        </FwButton>
      </div>
    </div>
  );
};

// ============================================================
// 3.2 — Schaal-sub-pagina (zwaarteniveau-keuze)
// ============================================================
const SchaalSubPagina = ({
  isFestival, vorm, heeftVarianten, huidigeRol, tariefrijen, gidsNiveaus,
  niveau, setNiveau, isValide, huidige, isNiveauVergrendeld,
  loadingAi, aiAdvies, aiError, setAiError, adviesValide,
  onTerug, onBevestig,
}) => {
  return (
    <div>
      <StepHeader
        tag={isFestival ? (heeftVarianten ? "Schaal · Zwaarteniveau" : "Schaal") : "Zwaarteniveau"}
        title="Welk niveau past bij jouw opdracht?"
        sub={isFestival
          ? (heeftVarianten
              ? `Je koos variant ${huidigeRol.festivaltype || "?"}. De bijbehorende schaal staat hieronder voorgeselecteerd — je kunt nog handmatig afwijken.`
              : "De Fair Pay richtlijn voor filmfestivals hanteert 12 schalen (gebaseerd op Cao Toneel & Dans). Voor deze rol staat de schaal vast — je kunt nog handmatig afwijken.")
          : "Het Opdracht Kompas deelt werk in op zwaarte (A t/m K). Elk niveau heeft een eigen minimum starttarief. We hebben alvast een niveau voorgesteld — check of het klopt."}
        onBack={onTerug}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 12 }}>
            Je rol: <span style={{ fontWeight: 400 }}>{huidigeRol.rol}</span> · {huidigeRol.dep}
            {!isFestival && gidsNiveaus.length > 1 && (
              <span style={{ marginLeft: 10, fontSize: 12, color: FW.navy2, fontWeight: 500 }}>
                Volgens de Gids past deze rol bij niveau {gidsNiveaus.join(" of ")}.
              </span>
            )}
          </div>

          <div style={{ background: FW.white, borderRadius: 20, padding: 20, border: `1.5px solid ${FW.gray100}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: FW.navy, marginBottom: 14, display: "flex", justifyContent: "space-between" }}>
              <span>Zwaarteniveau bij deze rol</span>
              <span style={{ color: FW.gray500, fontWeight: 500 }}>{isFestival ? "Schaal 1 → 12" : "Schaal A → K"}</span>
            </div>
            {(() => {
              const ascending = tariefrijen.slice().reverse();
              const tiers = isFestival
                ? [
                    { label: "Operationeel", items: ascending.slice(0, 4) },
                    { label: "Tactisch",     items: ascending.slice(4, 8) },
                    { label: "Strategisch",  items: ascending.slice(8, 12) },
                  ]
                : [
                    { label: "Operationeel", items: ascending.slice(0, 3) },
                    { label: "Tactisch",     items: ascending.slice(3, 7) },
                    { label: "Strategisch",  items: ascending.slice(7, 11) },
                  ];
              return (
                <div className="niveau-tiers" style={{ display: "grid", gridTemplateColumns: isFestival ? "repeat(12,1fr)" : "repeat(11,1fr)", gap: 4 }}>
                  {tiers.map(tier => (
                    <div key={tier.label} className="niveau-tier">
                      <div className="niveau-tier-label">{tier.label}</div>
                      <div className="niveau-tier-buttons">
                        {tier.items.map(r => {
                          const isGekozen = niveau === r.niveau;
                          const passendBijRol = gidsNiveaus.includes(r.niveau);
                          // Festival: 1/2/4 hebben géén functie en zijn hard
                          // vergrendeld. Film/AV: niveaus buiten Gids zijn
                          // klikbaar maar visueel gedempt.
                          const buitenBereik = isNiveauVergrendeld(r.niveau);
                          const vergrendeld = isFestival && buitenBereik;
                          const buitenGids   = !isFestival && buitenBereik;
                          const titel = vergrendeld
                            ? `${r.niveau}: geen festivalfunctie op deze schaal`
                            : buitenGids
                              ? `${r.niveau}: € ${r.tarief_min.toFixed(2)}/u — buiten Gids-advies voor ${huidigeRol.rol}`
                              : `${r.niveau}: € ${r.tarief_min.toFixed(2)}/u${passendBijRol ? ` — past bij ${huidigeRol.rol}` : ""}`;
                          return (
                            <button key={r.niveau}
                              onClick={() => { if (!vergrendeld) setNiveau(r.niveau); }}
                              disabled={vergrendeld}
                              aria-disabled={vergrendeld}
                              title={titel}
                              style={{
                                background: isGekozen ? FW.navy
                                  : (passendBijRol ? FW.beige2
                                    : (vergrendeld ? FW.gray50
                                      : (buitenGids ? FW.gray100 : FW.cream))),
                                color: isGekozen ? FW.yellow
                                  : (vergrendeld ? FW.gray500
                                    : (buitenGids ? FW.navy2 : FW.navy)),
                                border: `1.5px solid ${isGekozen ? FW.navy : (passendBijRol ? FW.taupe : FW.gray100)}`,
                                borderRadius: 10, padding: "14px 4px",
                                cursor: vergrendeld ? "not-allowed" : "pointer",
                                fontFamily: "var(--font-sans)", fontWeight: 700, fontSize: 18,
                                transition: "all .15s",
                                opacity: vergrendeld ? 0.45 : (buitenGids && !isGekozen ? 0.85 : 1),
                              }}>{r.niveau}</button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="niveau-legend-desktop" style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 12, color: FW.gray500 }}>
              <span>Operationeel</span>
              <span>Tactisch</span>
              <span>Strategisch</span>
            </div>
          </div>

          {isValide && (
            <div className="fw-card-mobile" style={{ marginTop: 20, padding: 18, background: FW.mint, borderRadius: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "baseline" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.navy, opacity: .75 }}>Niveau {huidige.niveau}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: FW.navy, marginTop: 2, lineHeight: 1.3 }}>
                    {!isFestival && huidige.domein}
                    {isFestival && huidige.rol_voorbeeld}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.navy, opacity: .75 }}>Minimum starttarief</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: FW.navy, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                    € {huidige.tarief_min.toFixed(2)}<span style={{ fontSize: 12, fontWeight: 500 }}>/uur</span>
                  </div>
                  {!isFestival && <div style={{ fontSize: 12, color: FW.navy, opacity: .7, marginTop: 2 }}>Bovenkant referentieschaal: € {huidige.tarief_max.toFixed(2)}/u</div>}
                </div>
              </div>
            </div>
          )}

          {!isValide && (
            <div style={{ marginTop: 20, padding: 24, background: FW.beige2, borderRadius: 20, border: `1.5px dashed ${FW.gray500}`, textAlign: "center", color: FW.navy2 }}>
              <i className="ri-arrow-up-line" style={{ fontSize: 22 }} />
              <div style={{ fontSize: 15, marginTop: 6, fontWeight: 600 }}>Kies een niveau in het schema hierboven om door te gaan.</div>
            </div>
          )}

          {isValide && !isFestival && (() => {
            const tekst = opdrachtKompasFilmAv[niveau]?.[huidigeRol.dep];
            if (!tekst) return null;
            const refMatch = tekst.match(/\n+\s*Referentie\/interview:\s*(.+)$/is);
            const hoofdtekst = refMatch ? tekst.slice(0, refMatch.index).trim() : tekst.trim();
            const referentie = refMatch ? refMatch[1].trim() : null;
            return (
              <div style={{ marginTop: 12, background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 14, padding: "18px 22px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: FW.navy, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <i className="ri-compass-3-line" style={{ fontSize: 16 }} />
                  Opdracht Kompas — {huidigeRol.dep} · niveau {niveau}
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.55, color: FW.navy2, whiteSpace: "pre-line" }}>
                  {hoofdtekst}
                </div>
                {referentie && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${FW.gray100}`, fontSize: 12, color: FW.gray500, fontStyle: "italic" }}>
                    Referentie/interview: {referentie}
                  </div>
                )}
              </div>
            );
          })()}

          {isValide && !isFestival && niveauIndicatorenFilmAv[niveau] && (
            <details style={{ marginTop: 12, background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 14, padding: "12px 18px" }}>
              <summary style={{ cursor: "pointer", fontSize: 14, fontWeight: 700, color: FW.navy, listStyle: "none", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="ri-arrow-right-s-line" style={{ fontSize: 18, transition: "transform .15s" }} />
                Niveau-indicatoren voor zelfstandig werkenden — niveau {niveau}
                <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 500, color: FW.gray500 }}>USB-punten {niveauIndicatorenFilmAv[niveau].usb}</span>
              </summary>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${FW.gray100}`, fontSize: 14, lineHeight: 1.55, color: FW.navy2 }}>
                {Object.entries(niveauIndicatorenFilmAv[niveau].secties).map(([key, tekst]) => (
                  <div key={key} style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, color: FW.navy, marginBottom: 3 }}>{sectieLabels[key]}</div>
                    <div>{tekst}</div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {loadingAi && (
            <div style={{ marginTop: 16, padding: 16, background: FW.beige2, borderRadius: 14, display: "flex", alignItems: "center", gap: 10 }}>
              <i className="ri-sparkling-2-line" style={{ fontSize: 18 }} />
              <span style={{ fontSize: 15, color: FW.navy }}>Even denken over een passend niveau voor jouw opdracht…</span>
            </div>
          )}
          {!loadingAi && aiError && (
            <FwInlineBanner type="warning" onDismiss={() => setAiError(false)} style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Niveau-advies mislukt — kies zelf het niveau in het schema hierboven.</div>
              {window.__lastAiError && (
                <div style={{ fontSize: 13, color: FW.navy2, fontFamily: "ui-monospace, monospace" }}>
                  {window.__lastAiError}
                </div>
              )}
            </FwInlineBanner>
          )}
          {aiAdvies && (
            <div style={{ marginTop: 16, padding: 18, background: "#FEF9B3", borderRadius: 14, border: `1.5px solid ${FW.yellow}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                <i className="ri-sparkling-2-line" /> Advies op basis van je omschrijving
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.5, color: FW.navy2 }}>
                Voorgesteld niveau: <b>{aiAdvies.niveau}</b> — {aiAdvies.reden}
              </div>
              {aiAdvies.twijfel && (
                <div style={{ marginTop: 10, fontSize: 14, color: FW.navy2, fontStyle: "italic" }}>
                  {aiAdvies.twijfel}
                </div>
              )}
              {!adviesValide && (
                <div style={{ marginTop: 10, fontSize: 13, color: "#8B1F1A", fontStyle: "italic" }}>
                  Het advies is geen geldig enkel niveau ({aiAdvies.niveau}). Kies zelf een niveau in het schema hierboven.
                </div>
              )}
              {adviesValide && niveau !== aiAdvies.niveau && (
                <button onClick={() => setNiveau(aiAdvies.niveau)} style={{
                  marginTop: 12, background: FW.navy, color: FW.yellow, border: 0, borderRadius: 999,
                  padding: "8px 14px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "var(--font-sans)",
                }}>Neem voorstel {aiAdvies.niveau} over</button>
              )}
            </div>
          )}
        </div>

        <AsideCard>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i className="ri-information-line" /> Hoe werkt de schaal?
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: FW.navy2 }}>
            {isFestival ? (
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 8, alignItems: "baseline" }}>
                <div style={{ fontWeight: 700, color: FW.navy }}>1 – 4</div>
                <div>Operationeel <span style={{ color: FW.gray500 }}>(office-medewerker, ondersteunend)</span></div>

                <div style={{ fontWeight: 700, color: FW.navy }}>5 – 8</div>
                <div>Tactisch <span style={{ color: FW.gray500 }}>(medewerker, coördinator, programmeur, hoofd A/B)</span></div>

                <div style={{ fontWeight: 700, color: FW.navy }}>9 – 12</div>
                <div>Strategisch <span style={{ color: FW.gray500 }}>(controller, hoofd C, algemeen/zakelijk/artistiek directeur)</span></div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 8, alignItems: "baseline" }}>
                <div style={{ fontWeight: 700, color: FW.navy }}>A, B, C</div>
                <div>Operationeel <span style={{ color: FW.gray500 }}>(bijv. productie runner, swing gang lid)</span></div>

                <div style={{ fontWeight: 700, color: FW.navy }}>D, E</div>
                <div>Tactisch/Operationeel <span style={{ color: FW.gray500 }}>(bijv. setbouwer, belichter)</span></div>

                <div style={{ fontWeight: 700, color: FW.navy }}>F, G</div>
                <div>Tactisch <span style={{ color: FW.gray500 }}>(bijv. focus puller, 1e boom operator)</span></div>

                <div style={{ fontWeight: 700, color: FW.navy }}>H, I</div>
                <div>Strategisch/Tactisch <span style={{ color: FW.gray500 }}>(bijv. acteur, production designer, DoP)</span></div>

                <div style={{ fontWeight: 700, color: FW.navy }}>J, K</div>
                <div>Strategisch <span style={{ color: FW.gray500 }}>(bijv. regisseur, showrunner, line producer)</span></div>
              </div>
            )}
          </div>
        </AsideCard>
      </div>

      <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        <FwButton variant="ghost" arrow={false} onClick={onTerug}>← Terug</FwButton>
        <FwButton variant="primary" onClick={onBevestig} disabled={!isValide}>
          {!isFestival
            ? "Bekijk je openingsbod"
            : vorm === "loondienst"
              ? "Verder naar dienstverband"
              : "Verder naar werkdagen"}
        </FwButton>
      </div>
    </div>
  );
};
